import { useState, useEffect, useCallback } from 'react'
import { api } from '../api'

interface VUDownloadSummary {
  id: number
  vehicle_id: number | null
  vehicle_registration: string | null
  filename: string
  source: string
  vu_generation: number
  vu_manufacturer: string | null
  vu_serial_number: string | null
  period_start: string | null
  period_end: string | null
  total_days: number | null
  total_driving_hours: number | null
  total_distance_km: number | null
  events_count: number
  faults_count: number
  over_speeding_count: number
  download_datetime: string | null
}

interface UploadResult {
  filename: string
  status: string
  message?: string
  db_id?: number
  vehicle_registration?: string
  period_start?: string
  period_end?: string
  total_driving_hours?: number
  total_distance_km?: number
  events_count?: number
  faults_count?: number
}

// ── Types for the analysis data ──

interface CompanyLock {
  lock_in: string
  lock_out: string | null
  company_name: string
  company_address: string
  is_current: boolean
}

interface CardDetail {
  driver_name: string
  card_number: string
  insertion_time_str: string
  withdrawal_time_str: string
  odometer_insert: number
  odometer_withdraw: number
  distance_km: number
}

interface DailyActivity {
  date: string
  driver_name: string
  card_number: string
  card_insertions: number
  driving_minutes: number
  work_minutes: number
  availability_minutes: number
  rest_minutes: number
  distance_km: number
  unaccounted_driving_minutes: number
  driving_source: 'speed_data' | 'estimated' | 'activity_changes'
  card_details: CardDetail[]
}

interface Calibration {
  date: string
  purpose: string
  workshop_name: string
  workshop_address: string
  vehicle_registration: string
  odometer_value: number
  next_calibration_date: string
}

interface ComplianceAlert {
  type: 'overdue' | 'due_soon' | 'no_card' | 'data_quality'
  severity: 'red' | 'amber' | 'green'
  title: string
  description: string
  date: string
}

// ── Company timeline color palette ──
const COMPANY_COLORS = [
  { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-100', border: 'border-blue-300' },
  { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-100', border: 'border-emerald-300' },
  { bg: 'bg-violet-500', text: 'text-violet-700', light: 'bg-violet-100', border: 'border-violet-300' },
  { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-100', border: 'border-amber-300' },
  { bg: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-100', border: 'border-rose-300' },
  { bg: 'bg-cyan-500', text: 'text-cyan-700', light: 'bg-cyan-100', border: 'border-cyan-300' },
  { bg: 'bg-lime-500', text: 'text-lime-700', light: 'bg-lime-100', border: 'border-lime-300' },
  { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-100', border: 'border-orange-300' },
  { bg: 'bg-indigo-500', text: 'text-indigo-700', light: 'bg-indigo-100', border: 'border-indigo-300' },
  { bg: 'bg-teal-500', text: 'text-teal-700', light: 'bg-teal-100', border: 'border-teal-300' },
]

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Strip the data:...;base64, prefix
      const base64 = result.split(',')[1] || result
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/** Find the index of the current company lock (is_current === true), or fallback to last entry */
function findCurrentCompanyIndex(locks: CompanyLock[]): number | null {
  if (locks.length === 0) return null
  const idx = locks.findIndex(l => l.is_current)
  return idx >= 0 ? idx : locks.length - 1
}

/** Build grouped dropdown options from company locks */
function buildCompanyDropdownOptions(locks: CompanyLock[]): { label: string; value: number; lock: CompanyLock }[] {
  // Count occurrences of each company name
  const nameCounts: Record<string, number> = {}
  const nameSeq: Record<string, number> = {}
  for (const lock of locks) {
    nameCounts[lock.company_name] = (nameCounts[lock.company_name] || 0) + 1
  }

  return locks.map((lock, i) => {
    const count = nameCounts[lock.company_name]
    nameSeq[lock.company_name] = (nameSeq[lock.company_name] || 0) + 1
    const fromDate = new Date(lock.lock_in).toLocaleDateString('en-GB')
    const toDate = lock.lock_out ? new Date(lock.lock_out).toLocaleDateString('en-GB') : 'Present'
    let label = lock.company_name
    if (count > 1) {
      label += ` (${nameSeq[lock.company_name]}/${count})`
    }
    label += ` — ${fromDate} to ${toDate}`
    if (lock.is_current) label += ' [Current]'
    return { label, value: i, lock }
  })
}

/** Format an ISO date string to YYYY-MM-DD for date inputs */
function toDateInputValue(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().split('T')[0]
}

export function VUDownloadsTab() {
  const [downloads, setDownloads] = useState<VUDownloadSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadResults, setUploadResults] = useState<UploadResult[] | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const [selectedDownload, setSelectedDownload] = useState<any | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  // selectedCompanyPeriod: index into companyLocks, or -1 for "All periods"
  const [selectedCompanyPeriod, setSelectedCompanyPeriod] = useState<number>(-1)
  const [filterDateFrom, setFilterDateFrom] = useState<string>('')
  const [filterDateTo, setFilterDateTo] = useState<string>('')

  const loadDownloads = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await api.listVUDownloads()
      setDownloads(Array.isArray(data) ? data : [])
    } catch (err: any) {
      setError(err.message || 'Failed to load VU downloads')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadDownloads() }, [loadDownloads])

  async function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList).filter(f => {
      const name = f.name.toLowerCase()
      return name.endsWith('.ddd') || name.endsWith('.dtg') || name.endsWith('.esm')
        || name.endsWith('.tgd') || name.endsWith('.v1b') || name.endsWith('.c1b')
    })

    if (files.length === 0) {
      setError('No valid tachograph files found. Accepted formats: .DDD, .DTG, .ESM, .TGD, .V1B, .C1B')
      return
    }

    setUploading(true)
    setUploadResults(null)
    setError('')

    try {
      // Convert files to base64 payloads
      const payloads: { filename: string; file_data: string }[] = []
      for (const file of files) {
        const b64 = await fileToBase64(file)
        payloads.push({ filename: file.name, file_data: b64 })
      }

      // Use bulk endpoint
      const result = await api.uploadVUFiles(payloads)
      setUploadResults(result.results || [])

      // Refresh the downloads list
      await loadDownloads()
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(true)
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }
  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
    }
    // Reset so same file can be re-selected
    e.target.value = ''
  }

  async function viewDetail(id: number) {
    setLoadingDetail(true)
    setSelectedCompanyPeriod(-1) // will be set properly once data loads
    setFilterDateFrom('')
    setFilterDateTo('')
    try {
      const data = await api.getVUDownload(id)
      setSelectedDownload(data)
      // Set default company period to current company
      const locks: CompanyLock[] = data?.analysis?.identification?.company_locks || []
      const currentIdx = findCurrentCompanyIndex(locks)
      if (currentIdx != null) {
        setSelectedCompanyPeriod(currentIdx)
        // Auto-populate date filters from current company period
        setFilterDateFrom(toDateInputValue(locks[currentIdx].lock_in))
        setFilterDateTo(toDateInputValue(locks[currentIdx].lock_out))
      } else {
        setSelectedCompanyPeriod(-1)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load download detail')
    } finally {
      setLoadingDetail(false)
    }
  }

  // ── Helper: format minutes as "Xh YYm" ──
  function fmtMins(mins: number | null | undefined): string {
    if (mins == null || mins === 0) return '—'
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
  }

  // ── Helper: driving source badge ──
  function drivingSourceBadge(source: string | undefined) {
    if (!source) return null
    const config: Record<string, { label: string; cls: string }> = {
      speed_data: { label: 'GPS', cls: 'bg-green-100 text-green-700' },
      estimated: { label: 'EST', cls: 'bg-amber-100 text-amber-700' },
      activity_changes: { label: 'ACT', cls: 'bg-blue-100 text-blue-700' },
    }
    const c = config[source]
    if (!c) return null
    return (
      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.cls}`}>
        {c.label}
      </span>
    )
  }

  // ── Helper: build compliance alerts ──
  function buildComplianceAlerts(
    calibrations: Calibration[],
    dailyActivity: DailyActivity[],
    today: Date
  ): ComplianceAlert[] {
    const alerts: ComplianceAlert[] = []
    const threeMonthsFromNow = new Date(today)
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3)

    // Calibration alerts
    if (calibrations.length > 0) {
      const latest = calibrations[calibrations.length - 1]
      if (latest.next_calibration_date) {
        const nextDue = new Date(latest.next_calibration_date)
        if (nextDue < today) {
          alerts.push({
            type: 'overdue',
            severity: 'red',
            title: 'Calibration Overdue',
            description: `Calibration was due ${latest.next_calibration_date}. Last performed by ${latest.workshop_name || 'unknown workshop'}.`,
            date: latest.next_calibration_date,
          })
        } else if (nextDue <= threeMonthsFromNow) {
          alerts.push({
            type: 'due_soon',
            severity: 'amber',
            title: 'Calibration Due Soon',
            description: `Next calibration due ${latest.next_calibration_date}. Schedule with an approved workshop.`,
            date: latest.next_calibration_date,
          })
        }
      }
    }

    // No-card driving alerts
    const noCardDays = dailyActivity.filter(d => (d.unaccounted_driving_minutes || 0) > 0)
    if (noCardDays.length > 0) {
      const totalNoCardMins = noCardDays.reduce((sum, d) => sum + (d.unaccounted_driving_minutes || 0), 0)
      alerts.push({
        type: 'no_card',
        severity: totalNoCardMins > 120 ? 'red' : 'amber',
        title: `No-Card Driving Detected (${noCardDays.length} day${noCardDays.length > 1 ? 's' : ''})`,
        description: `${fmtMins(totalNoCardMins)} total driving without a driver card inserted. Earliest: ${noCardDays[0]?.date || '—'}.`,
        date: noCardDays[0]?.date || '',
      })
    }

    // Data quality: distance but no driving
    const dataQualityDays = dailyActivity.filter(
      d => (d.distance_km || 0) > 0 && (d.driving_minutes || 0) === 0
    )
    if (dataQualityDays.length > 0) {
      alerts.push({
        type: 'data_quality',
        severity: 'amber',
        title: `Data Quality Warning (${dataQualityDays.length} day${dataQualityDays.length > 1 ? 's' : ''})`,
        description: `Distance recorded but 0 driving minutes on ${dataQualityDays.length} day(s). May indicate mode selector issues. First occurrence: ${dataQualityDays[0]?.date || '—'}.`,
        date: dataQualityDays[0]?.date || '',
      })
    }

    return alerts
  }

  // ── Helper: build driver summaries ──
  function buildDriverSummaries(dailyActivity: DailyActivity[]) {
    const driverMap: Record<string, {
      driver_name: string
      card_number: string
      total_driving_mins: number
      total_work_mins: number
      total_availability_mins: number
      total_rest_mins: number
      total_distance: number
      days: number
      day_list: DailyActivity[]
    }> = {}

    for (const day of dailyActivity) {
      const key = day.driver_name || day.card_number || 'Unknown'
      if (!driverMap[key]) {
        driverMap[key] = {
          driver_name: day.driver_name || 'Unknown',
          card_number: day.card_number || '—',
          total_driving_mins: 0,
          total_work_mins: 0,
          total_availability_mins: 0,
          total_rest_mins: 0,
          total_distance: 0,
          days: 0,
          day_list: [],
        }
      }
      driverMap[key].total_driving_mins += day.driving_minutes || 0
      driverMap[key].total_work_mins += day.work_minutes || 0
      driverMap[key].total_availability_mins += day.availability_minutes || 0
      driverMap[key].total_rest_mins += day.rest_minutes || 0
      driverMap[key].total_distance += day.distance_km || 0
      driverMap[key].days++
      driverMap[key].day_list.push(day)
    }

    return Object.values(driverMap).sort((a, b) => b.total_driving_mins - a.total_driving_mins)
  }

  // ── Helper: get unique company names from locks (for color assignment) ──
  function getCompanyColorMap(locks: CompanyLock[]): Map<string, typeof COMPANY_COLORS[0]> {
    const map = new Map<string, typeof COMPANY_COLORS[0]>()
    let colorIdx = 0
    for (const lock of locks) {
      if (!map.has(lock.company_name)) {
        map.set(lock.company_name, COMPANY_COLORS[colorIdx % COMPANY_COLORS.length])
        colorIdx++
      }
    }
    return map
  }

  // ── Helper: check if a date falls within the active date filter range ──
  function isDateInFilterRange(dateStr: string): boolean {
    if (!filterDateFrom && !filterDateTo) return true
    const d = dateStr // YYYY-MM-DD format, string comparison works
    if (filterDateFrom && d < filterDateFrom) return false
    if (filterDateTo && d > filterDateTo) return false
    return true
  }

  // ── Helper: check if a timestamp falls within the active date filter range ──
  function isTimestampInFilterRange(timestampStr: string): boolean {
    if (!timestampStr) return true
    if (!filterDateFrom && !filterDateTo) return true
    // Extract date portion from various timestamp formats
    const dateOnly = timestampStr.substring(0, 10)
    return isDateInFilterRange(dateOnly)
  }

  // Handle company period dropdown change
  function handleCompanyPeriodChange(value: number, locks: CompanyLock[]) {
    setSelectedCompanyPeriod(value)
    if (value >= 0 && locks[value]) {
      setFilterDateFrom(toDateInputValue(locks[value].lock_in))
      setFilterDateTo(toDateInputValue(locks[value].lock_out))
    } else {
      // "All periods" — clear date filters
      setFilterDateFrom('')
      setFilterDateTo('')
    }
  }

  // ── Detail view (Dashboard) ──
  if (selectedDownload) {
    const dl = selectedDownload
    const analysis = dl.analysis || {}
    const ident = analysis.identification || {}
    const summary = analysis.summary || {}
    const dailyActivity: DailyActivity[] = analysis.daily_activity || []
    const events = analysis.events || {}
    const speed = analysis.speed || {}
    const technical = analysis.technical || {}
    const companyLocks: CompanyLock[] = ident.company_locks || []

    const currentOperator = companyLocks.find(l => l.is_current)
    const companyColorMap = getCompanyColorMap(companyLocks)
    const today = new Date()

    const isFiltering = selectedCompanyPeriod >= 0 || filterDateFrom || filterDateTo

    // Filter daily activity by date range
    const filteredActivity = dailyActivity.filter(d => isDateInFilterRange(d.date))

    // Compute KPIs from filtered data
    const filteredTotalDays = filteredActivity.length
    const filteredTotalDrivingMins = filteredActivity.reduce((sum, d) => sum + (d.driving_minutes || 0), 0)
    const filteredTotalDrivingHours = filteredTotalDrivingMins / 60
    const filteredTotalDistance = filteredActivity.reduce((sum, d) => sum + (d.distance_km || 0), 0)

    const driverSummaries = buildDriverSummaries(filteredActivity)
    const calibrations: Calibration[] = technical.calibrations || []
    // Compliance alerts use filtered activity (except calibration which always uses full)
    const complianceAlerts = buildComplianceAlerts(calibrations, filteredActivity, today)

    // Build dropdown options
    const companyDropdownOptions = buildCompanyDropdownOptions(companyLocks)

    // Calculate the timeline span for the compact company locks bar
    const timelineDates = companyLocks.reduce((acc, lock) => {
      acc.push(new Date(lock.lock_in))
      if (lock.lock_out) acc.push(new Date(lock.lock_out))
      return acc
    }, [] as Date[])
    const timelineStart = timelineDates.length > 0 ? Math.min(...timelineDates.map(d => d.getTime())) : 0
    const timelineEnd = timelineDates.length > 0 ? Math.max(...timelineDates.map(d => d.getTime()), Date.now()) : Date.now()
    const timelineSpan = timelineEnd - timelineStart || 1

    // Filter events by date range
    const filteredEventsList = (events.events_list || []).filter((ev: any) =>
      isTimestampInFilterRange(ev.timestamp || ev.begin_time || '')
    )
    const filteredFaultsList = (events.faults_list || []).filter((fl: any) =>
      isTimestampInFilterRange(fl.timestamp || fl.begin_time || '')
    )

    // Filter over-speeding by date range
    const filteredOverSpeeding = (speed.over_speeding || []).filter((os: any) =>
      isTimestampInFilterRange(os.timestamp || os.begin_time || '')
    )

    return (
      <div>
        <button
          onClick={() => { setSelectedDownload(null); setSelectedCompanyPeriod(-1); setFilterDateFrom(''); setFilterDateTo('') }}
          className="mb-4 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          ← Back to downloads
        </button>

        {/* ═══════════════════════════════════════════════════════════════
            1. VEHICLE HEADER CARD
        ═══════════════════════════════════════════════════════════════ */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-bold text-2xl tracking-tight text-gray-900">
                {dl.vehicle_registration || ident.vehicle_registration || dl.filename}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <span className="font-mono text-xs">{ident.vin || technical.vin || '—'}</span>
                {(ident.manufacturer || technical.manufacturer || dl.vu_manufacturer) && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{ident.manufacturer || technical.manufacturer || dl.vu_manufacturer}</span>
                  </>
                )}
                <span className="text-gray-300">|</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  Gen {dl.vu_generation}
                </span>
              </div>
              {currentOperator && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-sm font-medium text-blue-800">{currentOperator.company_name}</span>
                  <span className="text-xs text-blue-500">Current operator</span>
                </div>
              )}
              {!currentOperator && ident.company_name && (
                <div className="mt-2 text-sm text-gray-600">
                  Operator: <span className="font-medium">{ident.company_name}</span>
                </div>
              )}
            </div>
            <div className="text-right text-xs text-gray-500 flex-shrink-0 ml-4">
              <div className="mb-1">
                <span className="text-gray-400">Downloaded</span>
                <div className="font-medium text-gray-700">
                  {ident.download_datetime
                    ? new Date(ident.download_datetime).toLocaleString('en-GB')
                    : dl.download_datetime
                      ? new Date(dl.download_datetime).toLocaleString('en-GB')
                      : '—'}
                </div>
              </div>
              <div>
                <span className="text-gray-400">Period</span>
                <div className="font-medium text-gray-700">
                  {summary.period_start || dl.period_start || '—'} → {summary.period_end || dl.period_end || '—'}
                </div>
              </div>
            </div>
          </div>

          {/* KPI Row — reflects FILTERED data */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Days</div>
              <div className="font-bold text-2xl text-gray-800 mt-1">{filteredTotalDays || '—'}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Driving</div>
              <div className="font-bold text-2xl text-green-700 mt-1">
                {filteredTotalDrivingHours > 0 ? `${filteredTotalDrivingHours.toFixed(1)}h` : '—'}
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-100">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Distance</div>
              <div className="font-bold text-2xl text-purple-700 mt-1">
                {filteredTotalDistance > 0 ? Math.round(filteredTotalDistance).toLocaleString() : '—'}
              </div>
              <div className="text-xs text-gray-400">km</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Max Speed</div>
              <div className="font-bold text-2xl text-blue-700 mt-1">
                {speed.max_speed_kmh != null ? `${speed.max_speed_kmh}` : '—'}
              </div>
              <div className="text-xs text-gray-400">km/h</div>
            </div>
            <div className="bg-sky-50 rounded-lg p-3 text-center border border-sky-100">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Avg Speed</div>
              <div className="font-bold text-2xl text-sky-700 mt-1">
                {speed.avg_speed_kmh != null ? `${speed.avg_speed_kmh}` : '—'}
              </div>
              <div className="text-xs text-gray-400">km/h</div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            2. COMPACT FILTER BAR — Company Dropdown + Date Range
        ═══════════════════════════════════════════════════════════════ */}
        {companyLocks.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex flex-wrap items-end gap-4">
              {/* Company period dropdown */}
              <div className="flex-1 min-w-[250px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Company Period</label>
                <select
                  value={selectedCompanyPeriod}
                  onChange={(e) => handleCompanyPeriodChange(parseInt(e.target.value, 10), companyLocks)}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={-1}>All periods</option>
                  {companyDropdownOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* From date */}
              <div className="w-[170px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* To date */}
              <div className="w-[170px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Clear filters button */}
              {isFiltering && (
                <button
                  onClick={() => handleCompanyPeriodChange(-1, companyLocks)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline pb-2"
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Compact timeline strip */}
            <div className="mt-3">
              <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
                {companyLocks.map((lock, i) => {
                  const lockStart = new Date(lock.lock_in).getTime()
                  const lockEnd = lock.lock_out ? new Date(lock.lock_out).getTime() : Date.now()
                  const leftPct = ((lockStart - timelineStart) / timelineSpan) * 100
                  const widthPct = Math.max(((lockEnd - lockStart) / timelineSpan) * 100, 0.5)
                  const colors = companyColorMap.get(lock.company_name) || COMPANY_COLORS[0]
                  const isSelected = selectedCompanyPeriod === i

                  return (
                    <button
                      key={i}
                      onClick={() => handleCompanyPeriodChange(selectedCompanyPeriod === i ? -1 : i, companyLocks)}
                      className={`absolute top-0 h-full ${colors.bg} hover:opacity-90 transition-all cursor-pointer
                        ${isSelected ? 'ring-2 ring-offset-1 ring-gray-800 z-10' : 'opacity-70'}
                      `}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      title={`${lock.company_name}: ${new Date(lock.lock_in).toLocaleDateString('en-GB')} — ${lock.lock_out ? new Date(lock.lock_out).toLocaleDateString('en-GB') : 'Active'}`}
                    />
                  )
                })}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-0.5 px-1">
                <span>{timelineDates.length > 0 ? new Date(timelineStart).toLocaleDateString('en-GB') : ''}</span>
                <span>{timelineDates.length > 0 ? new Date(timelineEnd).toLocaleDateString('en-GB') : ''}</span>
              </div>
            </div>

            {/* Filtering status line */}
            {isFiltering && (
              <div className="mt-2 text-[11px] text-gray-400">
                Showing {filteredActivity.length} of {dailyActivity.length} day{dailyActivity.length !== 1 ? 's' : ''}
                {selectedCompanyPeriod >= 0 && companyLocks[selectedCompanyPeriod] && (
                  <span> for <span className="font-medium text-gray-600">{companyLocks[selectedCompanyPeriod].company_name}</span></span>
                )}
                {filterDateFrom && <span> from {filterDateFrom}</span>}
                {filterDateTo && <span> to {filterDateTo}</span>}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            5. COMPLIANCE ALERTS PANEL (filtered)
        ═══════════════════════════════════════════════════════════════ */}
        {complianceAlerts.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm">
            <h4 className="font-semibold text-sm mb-3 text-gray-700">Compliance Alerts</h4>
            <div className="space-y-2">
              {complianceAlerts.map((alert, i) => {
                const severityStyles: Record<string, string> = {
                  red: 'bg-red-50 border-red-200 text-red-800',
                  amber: 'bg-amber-50 border-amber-200 text-amber-800',
                  green: 'bg-green-50 border-green-200 text-green-800',
                }
                const iconMap: Record<string, string> = {
                  overdue: '🔴',
                  due_soon: '🟡',
                  no_card: '🟠',
                  data_quality: '🟡',
                }
                return (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${severityStyles[alert.severity]}`}>
                    <span className="text-lg flex-shrink-0 mt-0.5">{iconMap[alert.type]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{alert.title}</div>
                      <div className="text-xs mt-0.5 opacity-80">{alert.description}</div>
                    </div>
                    {alert.date && (
                      <span className="text-xs opacity-60 flex-shrink-0">{alert.date}</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            3. DRIVER ACTIVITY CARDS (filtered)
        ═══════════════════════════════════════════════════════════════ */}
        {driverSummaries.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm">
            <h4 className="font-semibold text-sm mb-3 text-gray-700">
              Driver Activity ({driverSummaries.length} driver{driverSummaries.length > 1 ? 's' : ''})
              {isFiltering && <span className="text-gray-400 font-normal ml-1">— filtered</span>}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {driverSummaries.map((driver, i) => {
                const totalMins = driver.total_driving_mins + driver.total_work_mins + driver.total_availability_mins + driver.total_rest_mins
                const drivingPct = totalMins > 0 ? (driver.total_driving_mins / totalMins) * 100 : 0
                const workPct = totalMins > 0 ? (driver.total_work_mins / totalMins) * 100 : 0
                const availPct = totalMins > 0 ? (driver.total_availability_mins / totalMins) * 100 : 0
                const restPct = totalMins > 0 ? (driver.total_rest_mins / totalMins) * 100 : 0

                return (
                  <div key={i} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-sm text-gray-800">{driver.driver_name}</div>
                        <div className="text-[11px] text-gray-400 font-mono">{driver.card_number}</div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>{driver.days} day{driver.days > 1 ? 's' : ''}</div>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                      <div>
                        <span className="text-gray-400">Driving</span>
                        <div className="font-bold text-green-700">{fmtMins(driver.total_driving_mins)}</div>
                      </div>
                      <div>
                        <span className="text-gray-400">Distance</span>
                        <div className="font-bold text-purple-700">{Math.round(driver.total_distance).toLocaleString()} km</div>
                      </div>
                      <div>
                        <span className="text-gray-400">Work</span>
                        <div className="font-bold text-blue-700">{fmtMins(driver.total_work_mins)}</div>
                      </div>
                    </div>

                    {/* Stacked activity bar */}
                    {totalMins > 0 && (
                      <div>
                        <div className="flex h-3 rounded-full overflow-hidden">
                          {drivingPct > 0 && (
                            <div className="bg-green-500" style={{ width: `${drivingPct}%` }} title={`Driving: ${fmtMins(driver.total_driving_mins)}`} />
                          )}
                          {workPct > 0 && (
                            <div className="bg-blue-400" style={{ width: `${workPct}%` }} title={`Work: ${fmtMins(driver.total_work_mins)}`} />
                          )}
                          {availPct > 0 && (
                            <div className="bg-amber-300" style={{ width: `${availPct}%` }} title={`Availability: ${fmtMins(driver.total_availability_mins)}`} />
                          )}
                          {restPct > 0 && (
                            <div className="bg-gray-300" style={{ width: `${restPct}%` }} title={`Rest: ${fmtMins(driver.total_rest_mins)}`} />
                          )}
                        </div>
                        <div className="flex gap-3 mt-1.5 text-[10px] text-gray-400">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Driving</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />Work</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-300" />Avail</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" />Rest</span>
                        </div>
                      </div>
                    )}

                    {/* Top days list (up to 5) */}
                    {driver.day_list.length > 0 && (
                      <div className="mt-3 border-t border-gray-200 pt-2">
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Recent days</div>
                        <div className="space-y-0.5">
                          {driver.day_list.slice(0, 5).map((day, j) => (
                            <div key={j} className="flex items-center justify-between text-[11px] text-gray-600 py-0.5">
                              <span className="font-medium w-20">{day.date}</span>
                              <span className="text-green-700">{fmtMins(day.driving_minutes)}</span>
                              <span className="text-gray-400">{day.distance_km > 0 ? `${Math.round(day.distance_km)} km` : '—'}</span>
                              {drivingSourceBadge(day.driving_source)}
                            </div>
                          ))}
                          {driver.day_list.length > 5 && (
                            <div className="text-[10px] text-gray-400 pt-0.5">+{driver.day_list.length - 5} more days</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            4. DAILY ACTIVITY TABLE (filtered)
        ═══════════════════════════════════════════════════════════════ */}
        {filteredActivity.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm text-gray-700">
                Daily Activity ({filteredActivity.length} day{filteredActivity.length !== 1 ? 's' : ''})
                {isFiltering && <span className="text-gray-400 font-normal ml-1">— filtered</span>}
              </h4>
              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold">GPS</span>
                  Speed data
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">EST</span>
                  Estimated
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">ACT</span>
                  Activity changes
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-left border-b border-gray-200">
                    <th className="px-3 py-2 font-semibold text-gray-500">Date</th>
                    <th className="px-3 py-2 font-semibold text-gray-500">Driver</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-center">Src</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-center">Cards</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Driving</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Work</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Avail</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Rest</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">No-Card</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Distance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivity.slice(0, 150).map((day, i) => {
                    const hasNoCard = (day.unaccounted_driving_minutes || 0) > 0
                    const hasDistance = (day.distance_km || 0) > 0
                    const noDriving = (day.driving_minutes || 0) === 0
                    const isEstimated = day.driving_source === 'estimated'
                    return (
                      <tr
                        key={i}
                        className={`border-b border-gray-100 transition-colors hover:bg-gray-50
                          ${isEstimated ? 'bg-amber-50/40' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}
                          ${hasNoCard ? 'border-l-3 border-l-amber-400' : ''}
                        `}
                      >
                        <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{day.date || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 truncate max-w-[150px]">{day.driver_name || day.card_number || '—'}</td>
                        <td className="px-3 py-2 text-center">{drivingSourceBadge(day.driving_source)}</td>
                        <td className="px-3 py-2 text-center text-gray-400">{day.card_insertions || '—'}</td>
                        <td className={`px-3 py-2 text-right font-medium ${noDriving && hasDistance ? 'text-amber-600' : 'text-gray-800'}`}>
                          {fmtMins(day.driving_minutes)}
                          {noDriving && hasDistance && <span title="Distance recorded but no driving activity" className="ml-1 text-amber-500">!</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtMins(day.work_minutes)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtMins(day.availability_minutes)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtMins(day.rest_minutes)}</td>
                        <td className="px-3 py-2 text-right">
                          {hasNoCard ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                              {fmtMins(day.unaccounted_driving_minutes)}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {hasDistance ? `${Math.round(day.distance_km).toLocaleString()} km` : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {filteredActivity.length > 150 && (
                <div className="text-xs text-gray-500 mt-2 px-3">Showing first 150 of {filteredActivity.length} days</div>
              )}
            </div>
            <div className="mt-3 text-[11px] text-gray-400 px-1">
              Times shown are for activity recorded while the driver card was inserted. "No-Card" = VU driving time with no card present.
              {isEstimatedPresent(filteredActivity) && (
                <span className="ml-1">Rows with amber tint use estimated driving times.</span>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            6. CARD INSERTION TIMELINE (filtered by date range)
        ═══════════════════════════════════════════════════════════════ */}
        {filteredActivity.some((d: DailyActivity) => d.card_details?.length > 0) && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm">
            <h4 className="font-semibold text-sm mb-3 text-gray-700">
              Card Insertion Timeline
              {isFiltering && <span className="text-gray-400 font-normal ml-1">— filtered</span>}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-left border-b border-gray-200">
                    <th className="px-3 py-2 font-semibold text-gray-500">Date</th>
                    <th className="px-3 py-2 font-semibold text-gray-500">Driver</th>
                    <th className="px-3 py-2 font-semibold text-gray-500">Card Number</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-center">Inserted</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-center">Withdrawn</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Odo In</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Odo Out</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-right">Distance</th>
                    <th className="px-3 py-2 font-semibold text-gray-500 text-center">Src</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivity
                    .flatMap((day: DailyActivity) =>
                      (day.card_details || []).map((cd) => ({ ...cd, date: day.date, driving_source: day.driving_source }))
                    )
                    .sort((a, b) => {
                      // Sort chronologically by date then insertion time
                      const dateComp = (a.date || '').localeCompare(b.date || '')
                      if (dateComp !== 0) return dateComp
                      return (a.insertion_time_str || '').localeCompare(b.insertion_time_str || '')
                    })
                    .map((cd: any, i: number) => (
                      <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                        <td className="px-3 py-2 font-medium text-gray-800">{cd.date}</td>
                        <td className="px-3 py-2 text-gray-600">{cd.driver_name || '—'}</td>
                        <td className="px-3 py-2 font-mono text-gray-500 text-[11px]">{cd.card_number || '—'}</td>
                        <td className="px-3 py-2 text-center text-gray-700">{cd.insertion_time_str || '—'}</td>
                        <td className="px-3 py-2 text-center text-gray-700">{cd.withdrawal_time_str || <span className="text-green-600 font-medium">still in</span>}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{cd.odometer_insert != null ? cd.odometer_insert.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{cd.odometer_withdraw != null ? cd.odometer_withdraw.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-700">{cd.distance_km > 0 ? `${cd.distance_km} km` : '—'}</td>
                        <td className="px-3 py-2 text-center">{drivingSourceBadge(cd.driving_source)}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            7. EVENTS & FAULTS (filtered by date range)
        ═══════════════════════════════════════════════════════════════ */}
        {(filteredEventsList.length > 0 || filteredFaultsList.length > 0) && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-sm mb-2">
              Events &amp; Faults
              {isFiltering && <span className="text-gray-400 font-normal ml-1">— filtered</span>}
            </h4>
            {filteredEventsList.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-600 mb-1">Events ({filteredEventsList.length})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-2 py-1 font-medium">Date/Time</th>
                        <th className="px-2 py-1 font-medium">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEventsList.slice(0, 50).map((ev: any, i: number) => (
                        <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                          <td className="px-2 py-1">{ev.timestamp || ev.begin_time || '—'}</td>
                          <td className="px-2 py-1">{ev.event_type || ev.type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {filteredFaultsList.length > 0 && (
              <div>
                <div className="text-xs font-medium text-red-600 mb-1">Faults ({filteredFaultsList.length})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-red-50 text-left">
                        <th className="px-2 py-1 font-medium">Date/Time</th>
                        <th className="px-2 py-1 font-medium">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFaultsList.slice(0, 50).map((fl: any, i: number) => (
                        <tr key={i} className="bg-red-50/50">
                          <td className="px-2 py-1">{fl.timestamp || fl.begin_time || '—'}</td>
                          <td className="px-2 py-1">{fl.fault_type || fl.type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Over-speeding (filtered) ── */}
        {filteredOverSpeeding.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-sm mb-2 text-red-700">
              Over-speeding Events ({filteredOverSpeeding.length})
              {isFiltering && <span className="text-gray-400 font-normal ml-1">— filtered</span>}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-red-50 text-left">
                    <th className="px-2 py-1 font-medium">Date/Time</th>
                    <th className="px-2 py-1 font-medium text-right">Max Speed</th>
                    <th className="px-2 py-1 font-medium text-right">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOverSpeeding.map((os: any, i: number) => (
                    <tr key={i} className="bg-red-50/30">
                      <td className="px-2 py-1">{os.timestamp || os.begin_time || '—'}</td>
                      <td className="px-2 py-1 text-right font-medium text-red-700">
                        {os.max_speed || os.speed_max ? `${os.max_speed || os.speed_max} km/h` : '—'}
                      </td>
                      <td className="px-2 py-1 text-right">{os.duration_seconds != null ? `${os.duration_seconds}s` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            8. CALIBRATION HISTORY (always show full — NOT filtered)
        ═══════════════════════════════════════════════════════════════ */}
        {calibrations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-sm mb-2">Calibration History ({calibrations.length})</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-2 py-1 font-medium">Date</th>
                    <th className="px-2 py-1 font-medium">Purpose</th>
                    <th className="px-2 py-1 font-medium">Workshop</th>
                    <th className="px-2 py-1 font-medium">Address</th>
                    <th className="px-2 py-1 font-medium">Vehicle</th>
                    <th className="px-2 py-1 font-medium text-right">Odometer</th>
                    <th className="px-2 py-1 font-medium">Next Due</th>
                  </tr>
                </thead>
                <tbody>
                  {calibrations.map((cal, i) => {
                    const isOverdue = cal.next_calibration_date && new Date(cal.next_calibration_date) < today
                    const threeMonths = new Date(today)
                    threeMonths.setMonth(threeMonths.getMonth() + 3)
                    const isDueSoon = cal.next_calibration_date && !isOverdue && new Date(cal.next_calibration_date) <= threeMonths
                    return (
                      <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                        <td className="px-2 py-1 font-medium">{cal.date || '—'}</td>
                        <td className="px-2 py-1 capitalize">{cal.purpose || '—'}</td>
                        <td className="px-2 py-1">{cal.workshop_name || '—'}</td>
                        <td className="px-2 py-1 text-gray-500">{cal.workshop_address || '—'}</td>
                        <td className="px-2 py-1">{cal.vehicle_registration || '—'}</td>
                        <td className="px-2 py-1 text-right">{cal.odometer_value != null ? `${cal.odometer_value.toLocaleString()} km` : '—'}</td>
                        <td className={`px-2 py-1 ${isOverdue ? 'text-red-600 font-bold' : isDueSoon ? 'text-amber-600 font-medium' : ''}`}>
                          {cal.next_calibration_date || '—'}
                          {isOverdue && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-semibold">OVERDUE</span>
                          )}
                          {isDueSoon && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 font-semibold">DUE SOON</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {/* Latest calibration summary */}
            {calibrations.length > 0 && (() => {
              const latest = calibrations[calibrations.length - 1]
              const nextDue = latest.next_calibration_date
              const isOverdue = nextDue && new Date(nextDue) < today
              return (
                <div className={`mt-3 text-xs p-2 rounded ${isOverdue ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  Latest calibration: {latest.date} by {latest.workshop_name}
                  {nextDue && (
                    <span>
                      {isOverdue
                        ? ` — Next calibration OVERDUE (was due ${nextDue})`
                        : ` — Next due: ${nextDue}`
                      }
                    </span>
                  )}
                </div>
              )
            })()}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            9. TECHNICAL INFORMATION (always show full — NOT filtered)
        ═══════════════════════════════════════════════════════════════ */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-sm mb-2">Technical Information</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-gray-500">VU Manufacturer</div>
              <div className="font-medium">{ident.manufacturer || technical.manufacturer || dl.vu_manufacturer || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">VIN</div>
              <div className="font-medium font-mono">{ident.vin || technical.vin || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500">Registration</div>
              <div className="font-medium">{ident.vehicle_registration || dl.vehicle_registration || '—'}</div>
            </div>
            {technical.tyre_size && (
              <div>
                <div className="text-gray-500">Tyre Size</div>
                <div className="font-medium">{technical.tyre_size}</div>
              </div>
            )}
            <div>
              <div className="text-gray-500">Generation</div>
              <div className="font-medium">Gen {dl.vu_generation}</div>
            </div>
            <div>
              <div className="text-gray-500">Source File</div>
              <div className="font-medium truncate max-w-[200px]" title={dl.filename}>{dl.filename}</div>
            </div>
            {ident.serial_number && (
              <div>
                <div className="text-gray-500">Serial Number</div>
                <div className="font-medium font-mono">{ident.serial_number}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Main view: upload + downloads list ──
  return (
    <div>
      {/* Upload zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 transition-colors ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
        }`}
      >
        <div className="text-3xl mb-2">📥</div>
        <div className="text-sm font-medium text-gray-700 mb-1">
          {uploading ? 'Uploading and parsing files...' : 'Drop DDD files here or click to browse'}
        </div>
        <div className="text-xs text-gray-500 mb-3">
          Plug in your DigiDown / TachoSafe device and select the DDD files, or drag them from the device folder
        </div>
        {!uploading && (
          <div className="flex items-center justify-center gap-2">
            <label className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">
              Select Files
              <input
                type="file"
                multiple
                accept=".ddd,.DDD,.dtg,.DTG,.esm,.ESM,.tgd,.TGD,.v1b,.V1B,.c1b,.C1B"
                onChange={onFileInput}
                className="hidden"
              />
            </label>
            <label className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 cursor-pointer">
              Select Folder
              <input
                type="file"
                // @ts-ignore — webkitdirectory is non-standard but supported in Chrome/Edge/Firefox
                webkitdirectory=""
                onChange={onFileInput}
                className="hidden"
              />
            </label>
          </div>
        )}
        {uploading && (
          <div className="flex items-center justify-center gap-2 text-blue-600">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing...
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-4">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Upload results */}
      {uploadResults && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-sm mb-2">
            Upload Results — {uploadResults.filter(r => r.status === 'success').length} succeeded,{' '}
            {uploadResults.filter(r => r.status !== 'success').length} failed
          </h4>
          <div className="space-y-1">
            {uploadResults.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${r.status === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                <span className={r.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                  {r.status === 'success' ? '✓' : '✕'}
                </span>
                <span className="font-medium">{r.filename}</span>
                {r.status === 'success' ? (
                  <>
                    <span className="text-gray-500">→</span>
                    <span>{r.vehicle_registration || 'Unknown vehicle'}</span>
                    <span className="text-gray-400">·</span>
                    <span>{r.period_start} – {r.period_end}</span>
                    <span className="text-gray-400">·</span>
                    <span>{r.total_driving_hours?.toFixed(1)}h driving</span>
                    <span className="text-gray-400">·</span>
                    <span>{Math.round(r.total_distance_km || 0).toLocaleString()} km</span>
                  </>
                ) : (
                  <span className="text-red-600">{r.message}</span>
                )}
              </div>
            ))}
          </div>
          <button onClick={() => setUploadResults(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">
            Dismiss
          </button>
        </div>
      )}

      {/* Downloads table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-sm">Vehicle Unit Downloads</h3>
          <div className="flex items-center gap-3">
            {downloads.length > 0 && (
              <button
                onClick={async () => {
                  if (!confirm('Delete ALL VU downloads? This cannot be undone.')) return
                  try {
                    await api.deleteAllVUDownloads()
                    await loadDownloads()
                  } catch (err: any) {
                    setError(err.message || 'Failed to delete downloads')
                  }
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Delete All
              </button>
            )}
            <button
              onClick={loadDownloads}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {downloads.length === 0 && !loading ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No VU downloads yet. Upload DDD files from your download device to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">File</th>
                  <th className="px-3 py-2 font-medium">Gen</th>
                  <th className="px-3 py-2 font-medium">Period</th>
                  <th className="px-3 py-2 font-medium text-right">Days</th>
                  <th className="px-3 py-2 font-medium text-right">Driving</th>
                  <th className="px-3 py-2 font-medium text-right">Distance</th>
                  <th className="px-3 py-2 font-medium text-right">Events</th>
                  <th className="px-3 py-2 font-medium text-right">Faults</th>
                  <th className="px-3 py-2 font-medium">Uploaded</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((dl, i) => (
                  <tr key={dl.id} className={`hover:bg-blue-50 cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    onClick={() => viewDetail(dl.id)}>
                    <td className="px-3 py-2 font-medium">{dl.vehicle_registration || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 truncate max-w-[120px]">{dl.filename}</td>
                    <td className="px-3 py-2">{dl.vu_generation}</td>
                    <td className="px-3 py-2">{dl.period_start || '—'} → {dl.period_end || '—'}</td>
                    <td className="px-3 py-2 text-right">{dl.total_days ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{dl.total_driving_hours != null ? `${dl.total_driving_hours.toFixed(1)}h` : '—'}</td>
                    <td className="px-3 py-2 text-right">{dl.total_distance_km != null ? `${Math.round(dl.total_distance_km).toLocaleString()} km` : '—'}</td>
                    <td className="px-3 py-2 text-right">{dl.events_count}</td>
                    <td className={`px-3 py-2 text-right ${dl.faults_count > 0 ? 'text-red-600 font-medium' : ''}`}>{dl.faults_count}</td>
                    <td className="px-3 py-2 text-gray-500">
                      {dl.download_datetime ? new Date(dl.download_datetime).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-3 py-2 flex items-center gap-2">
                      <button className="text-blue-600 hover:text-blue-800" onClick={(e) => { e.stopPropagation(); viewDetail(dl.id) }}>
                        View
                      </button>
                      <button
                        className="text-red-400 hover:text-red-600"
                        onClick={async (e) => {
                          e.stopPropagation()
                          if (!confirm(`Delete download for ${dl.vehicle_registration || dl.filename}?`)) return
                          try {
                            await api.deleteVUDownload(dl.id)
                            await loadDownloads()
                          } catch (err: any) {
                            setError(err.message || 'Failed to delete')
                          }
                        }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loadingDetail && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg flex items-center gap-3">
            <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Loading download details...
          </div>
        </div>
      )}
    </div>
  )
}

// ── Standalone helper (used inside JSX as a function call) ──
function isEstimatedPresent(activity: DailyActivity[]): boolean {
  return activity.some(d => d.driving_source === 'estimated')
}
